/**
 * Operator BigCommerce fulfilment/tracking planning.
 * Local tracking is never treated as provider confirmation.
 */

export const PROVIDER_SYNC_LOCAL = "local_recorded";
export const PROVIDER_SYNC_PENDING = "pending";
export const PROVIDER_SYNC_CONFIRMED = "confirmed";
export const PROVIDER_SYNC_ERROR = "error";

export type ProviderSyncStatus =
  | typeof PROVIDER_SYNC_LOCAL
  | typeof PROVIDER_SYNC_PENDING
  | typeof PROVIDER_SYNC_CONFIRMED
  | typeof PROVIDER_SYNC_ERROR;

export const MANUAL_SHIPPING_CARRIERS = [
  { code: "auspost", label: "Australia Post" },
  { code: "other", label: "Other / custom" },
] as const;

export type ManualShippingCarrierCode = (typeof MANUAL_SHIPPING_CARRIERS)[number]["code"];

export function mapCarrierToBcShippingProvider(carrierCode: string | null | undefined, customLabel?: string | null): {
  carrier_code: ManualShippingCarrierCode | string;
  shipping_provider: string;
  shipping_method: string;
} {
  const code = (carrierCode ?? "auspost").trim().toLowerCase() || "auspost";
  if (code === "auspost" || code === "australia post" || code === "australia_post") {
    return { carrier_code: "auspost", shipping_provider: "auspost", shipping_method: "Australia Post" };
  }
  const method = (customLabel ?? "").trim() || "Other";
  return { carrier_code: "other", shipping_provider: "", shipping_method: method };
}

export function isAuthorizedBigCommerceConnector(opts: {
  appCredentialsStatus?: string | null;
  configAuthStatus?: string | null;
  connectorAuthStatus?: string | null;
  enabled?: boolean | null;
  credentialRef?: string | null;
}): boolean {
  void opts.enabled;
  void opts.credentialRef;
  if ((opts.appCredentialsStatus ?? "") === "not_configured") return false;
  const auth = String(opts.configAuthStatus || opts.connectorAuthStatus || "").trim();
  return auth === "connected" || auth === "authorized";
}

export function isProviderConfirmed(opts: {
  providerSyncStatus?: string | null;
  providerShipmentId?: string | number | null;
}): boolean {
  return opts.providerSyncStatus === PROVIDER_SYNC_CONFIRMED && opts.providerShipmentId != null && String(opts.providerShipmentId).length > 0;
}

export function localTrackingMeansProviderConfirmed(_trackingNumber: string | null | undefined): boolean {
  return false;
}

export function shouldSkipBcShipmentPost(opts: {
  existingRemote: Array<{ tracking_number?: string | null; id?: number }>;
  trackingNumber: string;
}): { skip: boolean; externalShipmentId?: number } {
  const tracking = opts.trackingNumber.trim();
  const match = opts.existingRemote.find((s) => (s.tracking_number || "") === tracking);
  if (!match) return { skip: false };
  return { skip: true, externalShipmentId: match.id };
}

/** Provider sync never creates Tradivo fulfilments or shipments. */
export function shouldInsertLocalFulfilmentFromProviderSync(_opts?: {
  existingFulfilmentId?: string | null;
}): boolean {
  return false;
}

export type TradivoFulfilmentLine = { line_id: string; sku: string | null; qty: number };
export type BcOrderProduct = { id: number; sku?: string | null; quantity?: number };

export function mapFulfilmentLinesToBcShipmentItems(opts: {
  tradivoLines: TradivoFulfilmentLine[];
  bcProducts: BcOrderProduct[];
}):
  | { ok: true; items: Array<{ external_line_id: number; qty: number; line_id: string }> }
  | { ok: false; code: string; message: string } {
  const lines = opts.tradivoLines.filter((l) => l.qty > 0);
  if (lines.length === 0) {
    return { ok: false, code: "NO_FULFILLED_LINES", message: "No fulfilled lines to send to BigCommerce." };
  }
  const items: Array<{ external_line_id: number; qty: number; line_id: string }> = [];
  for (const line of lines) {
    const sku = (line.sku ?? "").trim().toLowerCase();
    if (!sku) {
      return { ok: false, code: "LINE_SKU_MISSING", message: "A fulfilled line is missing SKU; cannot map to a BigCommerce order product." };
    }
    const matches = opts.bcProducts.filter((p) => String(p.sku ?? "").trim().toLowerCase() === sku);
    if (matches.length !== 1) {
      return {
        ok: false,
        code: "LINE_SKU_UNMAPPED",
        message: `Could not uniquely map SKU ${line.sku} to a BigCommerce order product.`,
      };
    }
    const bc = matches[0];
    if (!Number.isInteger(bc.id) || bc.id <= 0) {
      return { ok: false, code: "LINE_SKU_UNMAPPED", message: `BigCommerce order product id missing for SKU ${line.sku}.` };
    }
    items.push({ external_line_id: bc.id, qty: line.qty, line_id: line.line_id });
  }
  if (items.length === 0) {
    return { ok: false, code: "EMPTY_SHIPMENT_ITEMS", message: "Refusing an empty BigCommerce shipment item list." };
  }
  return { ok: true, items };
}

export function assertNonEmptyBcShipmentItems(items: unknown): { ok: true } | { ok: false; code: string; message: string } {
  if (!Array.isArray(items) || items.length === 0) {
    return { ok: false, code: "EMPTY_SHIPMENT_ITEMS", message: "BigCommerce shipment items must not be empty." };
  }
  return { ok: true };
}

export function buildBcOperatorShipmentBody(opts: {
  orderAddressId: number;
  trackingNumber: string;
  shippingProvider: string;
  shippingMethod: string;
  items: Array<{ external_line_id: number; qty: number }>;
}): Record<string, unknown> {
  return {
    order_address_id: opts.orderAddressId,
    tracking_number: opts.trackingNumber,
    shipping_provider: opts.shippingProvider,
    shipping_method: opts.shippingMethod,
    items: opts.items.map((it) => ({
      order_product_id: it.external_line_id,
      quantity: it.qty,
    })),
  };
}

export type BcShipmentPostFailureClass = "uncertain_external_result";

/**
 * Classify a shipment POST that has already started.
 * HTTP status, including 4xx, does not prove BigCommerce created no shipment.
 * Non-success stays uncertain until trusted reconciliation supplies evidence.
 */
export function classifyBigCommerceShipmentPostFailure(_status: number): BcShipmentPostFailureClass {
  return "uncertain_external_result";
}
